import { PlatformLocation } from '@angular/common';
import { Inject, Optional, Directive } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import * as moment from 'moment';
import { filter } from 'rxjs/operators';
import { ProhibitionService } from '../prohibition/services/prohibition.service';
import { BaseComponent } from '../shared/base-components/base.component';
import { MvdModalService } from '../shared/services/modal/mvd-modal.service';
import { IncidentType } from './models/enums/incident-type.enum';
import { IncidentTypeModel } from './models/incident-type.model';
import { IncidentModel } from './models/incident.model';
import { EnumDataService } from './services/enums.DataService';
import { IncidentTypeService } from './services/incident-type.service';
import { IncidentService } from './services/incident.service';
import { IncidentDialogContext } from './shared/incident-dialog-context.model';

@Directive()
export abstract class IncidentModalComponent<TModel extends IncidentModel> extends BaseComponent {
    public showButtonLoading = false;
    public incidentForm: UntypedFormGroup;
    public showReferenceNumber = false;
    public showEditReceivedDate = true;
    public showEditReferenceNumber = true;
    public referenceNumberDisplayValue: string;
    public caughtChange: any[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];
    public hasCdlCheckboxOnForm = false;
    private selectedIncidentType: IncidentTypeModel;
    public isConviction = false;
    public isAbv = false;
    public isLawEnforcement = false;
    public showUserInput = false;
    public lastOccurenceDate: any;
    public showBac = false;
    public showBacNotRequired = false;
    public lastReevaluatedBy = '';
    public derivedCdl = false;
    public derivedCdlSet = false;
    public cdlRegistered = 0;
    public incidentType: IncidentTypeModel[];

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: IncidentDialogContext<TModel>,
        public prohibitionService: ProhibitionService,
        public formBuilder: UntypedFormBuilder,
        public dialogRef: MatDialogRef<IncidentModalComponent<TModel>>,
        public modalService: MvdModalService,
        public incidentService: IncidentService,
        public incidentDataService: EnumDataService,
        public incidentTypeService: IncidentTypeService,
        private location: PlatformLocation
    ) {
        super();
        this.incidentType = new Array<IncidentTypeModel>();
        this.location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    protected get isNewIncident(): boolean {
        return this.data.incident.incidentNumberId == 0;
    }

    public ngOnInit(): void {
        const form: any = {};
        this.onInit(form);
        form.occurrenceDate = ['', Validators.required];
        form.isEventWOProhibition = [''];
        form.isDrugsInvolved = [''];
        // To mark a incident as expunged for MVR
        form.isExpunged = [''];
        form.phoneNumberText = [''];
        form.usePhoneText = [''];
        form.lawEnfNameSelect = [''];
        form.sentencingAuthNameSelect = [''];
        form.incidentStatusTypeId = [''];

        if (this.isLawEnforcement || this.isConviction) {
            form.accidentTypeId = ['', Validators.required];
        }

        if (this.isConviction) {
            form.citingAgencyTypeId = ['', Validators.required];
            form.offenseClassificationTypeId = ['', Validators.required];
            form.dispositionDate = ['', Validators.required];
            form.courtId = ['', Validators.required];
        } else {
            form.offenseClassificationTypeId = [''];
        }

        if (this.isAbv) {
            form.landCategoryTypeId = [{ value: '', disabled: this.data.isReadOnly }, Validators.required];
            form.vin = [
                { value: '', disabled: this.data.isReadOnly || !!this.data.incident.incidentNumberId },
                [Validators.required, Validators.maxLength(25)]
            ];
        }

        form.plateTypeId = [''];
        form.vehicleRegistrationJurisdictionId = [''];
        form.alcoholClassReqCheck = [''];
        form.driverLicenseNumber = [''];
        form.driverLicenseJurisdiction = [''];
        form.printLetter = [true];
        form.referenceNumber = this.showReferenceNumber ? ['', Validators.required] : [''];
        form.receivedDate = this.showEditReceivedDate ? ['', Validators.required] : [''];
        this.incidentForm = this.formBuilder.group(form);

        this.incidentService.getAllIncidentTypes()
            .subscribe(r => {
                this.incidentType = r.result;

                // ********************************JCMCONFIG*************************************/
                if (this.data.incident.incidentGroupTypeId != undefined) {
                    this.getIncidentTypeByGroup(this.data.incident.incidentGroupTypeId);
                } else {
                    this.getCorrectIncidentType();
                }
                // ********************************END JCMCONFIG*************************************/

                this.incidentForm = this.formBuilder.group(form);
                this.onLoadLookups((e?) => {
                    this.onLoadLookupsCompleted(e);
                });

                if (this.data.isReadOnly) {
                    this.incidentForm.get('driverLicenseNumber').disable();
                    this.incidentForm.get('driverLicenseJurisdiction').disable();
                }

                if (this.data.incident.incidentNumberId == 0) {
                    this.incidentForm.get('occurrenceDate').valueChanges.subscribe(v => {
                        var utc = moment(v, 'MM-DD-YYYY', true);
                        var isUTC = utc.isValid();
                        if (isUTC && this.lastOccurenceDate != v) {
                            this.lastOccurenceDate = v;
                            this.occurrenceDateChanged(v);
                        }
                    });
                } else {
                    this.cdlRegistered = 0;
                }

                this.reevaluatedBy();
            });
    }

    async getIncidentTypeByGroup(incidentGroupTypeId: any) {
        await this.incidentService.getIncidentTypeByGroup(incidentGroupTypeId).subscribe(r => {
            this.selectedIncidentType = r;
            this.data.incident.incidentTypeId = this.selectedIncidentType.id;
            this.data.incident.violationCode = this.selectedIncidentType.violationCode;
            this.data.incident.acdCodeId = this.selectedIncidentType.acdCodeId;

            var refType = this.selectedIncidentType.incidentReferenceTypeId;
            this.showReferenceNumber = refType != 5;
            this.onGetIncidentTypeCompleted(this.selectedIncidentType, false);
        }, e => this.handleError(e));
    }

    async getCorrectIncidentType() {
        this.handleValidationResult('');
        await this.incidentService.getCorrectIncidentType(this.data.incident).subscribe(r => {
            this.selectedIncidentType = r;
            this.data.incident.incidentTypeId = this.selectedIncidentType.id;
            this.data.incident.violationCode = this.selectedIncidentType.violationCode;
            this.data.incident.acdCodeId = this.selectedIncidentType.acdCodeId;
            this.data.incident.incidentTypeDescription = this.selectedIncidentType.incidentTypeDescription;
            this.onGetIncidentTypeCompleted(this.selectedIncidentType, false);
        }, e => this.handleValidationResult(e));

    }

    protected onInit(form: any): void {

    }

    public catchChange(value: any, index: number): void {
        this.caughtChange[index] = value;
    }

    public catchNamedChange(value: any, index: number): void {
        this.caughtChange[index] = value;

        if (value != null && value != undefined && this.hasCdlCheckboxOnForm) {
            var v = this.incidentForm.get(value).value;

            if (v != null && v != undefined && this.derivedCdlSet == true) {
                if (v != this.derivedCdl) {
                    this.data.incident.csrCdlOverride = false;
                    this.modalService.confirm()
                        .title('CDL Holder Confirmation')
                        .body(' MAX has derived a different value for CDL Holder indicator than what you have selected, do you want to continue? ')
                        .okBtn('Yes')
                        .cancelBtn('No')
                        .open()
                        .afterClosed()
                        .pipe(filter(result => !!result))
                        .subscribe(() => {
                            this.data.incident.csrCdlOverride = true;
                        });
                } else {
                    this.data.incident.csrCdlOverride = false;
                }
            }
        }
    }

    protected onLoadLookups(callback: (error?) => void): void {
        callback();
    }

    protected abstract onLoadIncident(callback: (error?) => void): void;
    protected abstract onSaveIncident(callback: (error?: any) => void): void;
    protected abstract onDeleteIncident(callback: () => void): void;

    protected onLoaded(updateReference: boolean = true): void {
        this.incidentForm.get('occurrenceDate').setValue(this.data.incident.occurrenceDate);
        this.incidentForm.get('isExpunged').setValue(this.data.incident.isExpunged);
        this.incidentForm.get('printLetter').setValue(!this.data.incident.hasSkippedLetterGeneration);
        this.incidentForm.get('receivedDate').setValue(this.data.incident.receivedDate);
        if (this.data.incident.id == 0) {
            this.incidentForm.get('incidentStatusTypeId').setValue(1);
            this.data.incident.incidentStatusTypeId = 1;
            this.incidentForm.get('incidentStatusTypeId').disable();
        } else {
            this.incidentForm.get('incidentStatusTypeId').setValue(this.data.incident.incidentStatusTypeId);
        }
        if (this.data.incident.incidentStatusTypeId == 4) {
            this.incidentForm.addControl('incidentStatusReasonTypeId', new UntypedFormControl(''));
            this.incidentForm.get('incidentStatusReasonTypeId').setValue(this.data.incident.incidentStatusReasonTypeId);
        }

        this.incidentForm.get('referenceNumber').setValidators((this.showReferenceNumber ? [Validators.required] : []));

        if (updateReference) {
            this.incidentForm.get('referenceNumber').setValue(this.data.incident.referenceNumber);
        }

        this.incidentForm.get('isEventWOProhibition').setValue(this.data.incident.isEventWOProhibition);

        // ******************Handle TSS Incident****************************
        if (this.data.incident.incidentTypeId == IncidentType.TrafficSurvivalSchoolRequired) {
            this.showEditReferenceNumber = false;

            if (this.data.incident.incidentNumberId == 0) {

                this.data.incident.referenceNumber = 'to be assigned';
                this.incidentForm.get('referenceNumber').setValue('to be assigned');
            }
        }

        if (this.data.incident.incidentTypeId == IncidentType.NSFPenaltyDue) {
            this.incidentForm.get('referenceNumber').disable();
        }

        // ******************Handle TSS Incident****************************

        // ******************Handle BAC****************************
        if (this.data.incident.incidentTypeId == IncidentType.AdminPerSe) {
            this.showBac = true;
        }

        if (this.data.incident.acdCodeId == 4) {
            this.showBac = true;
            this.showBacNotRequired = false;
        } else {
            this.showBacNotRequired = true;
            this.showBac = false;
        }

        if (this.data.incident.incidentTypeId == IncidentType.AdminPerSeDrugs) {
            this.incidentForm.get('isDrugsInvolved').setValue(true);
        }
        // ******************Handle BAC****************************

        if (this.data.incident.hasProhibition) {
            this.incidentForm.get('isEventWOProhibition').disable();
        }

        // ****************** Get the CDL Status ******************
        if (this.hasCdlCheckboxOnForm) {
            try {
                this.incidentService.getCDLStatusOnDate(this.data.incident).subscribe(r => {
                    this.derivedCdl = r.result;
                    this.derivedCdlSet = true;
                });
            } catch (ex) {
                console.error(ex);
            }
        }

        this.incidentForm.updateValueAndValidity();
    }

    private onLoadLookupsCompleted(error?: any): void {
        if (error) {
            this.handleError(error);
            return;
        }

        this.incidentTypeService.getIncidentType(this.data.incident.incidentTypeId)
            .subscribe(
                r => this.onGetIncidentTypeCompleted(r),
                e => this.handleError(e));
    }

    private onGetIncidentTypeCompleted(incidentType: IncidentTypeModel, updateReference: boolean = true): void {
        this.referenceNumberDisplayValue = incidentType.incidentReferenceTypeDisplayValue;

        if (this.data.incident.incidentNumberId == 0 && this.data.incident.convictionRequestId == 0) {
            this.showUserInput = true;
            this.data.incident.violationCode = incidentType.violationCode;
            this.data.incident.incidentTypeDescription = (<any> incidentType).incidentTypeDescription;
            this.data.incident.incidentTypeDisplayValue = (<any> incidentType).incidentTypeDisplayValue;
            this.data.incident.acdCodeId = incidentType.acdCodeId;

            if (this.data.incident.acdCodeId > 0) {
                this.incidentTypeService.getACD(this.data.incident.acdCodeId).subscribe(r => {
                    this.data.incident.acdCodeDisplayValue = r.acdCodeDisplayValue + '-' + r.acdCodeDescription;
                },
                e => this.handleError(e));
            }

            this.onLoaded(updateReference);
        } else {
            this.onLoadIncident((e?) => {
                this.onLoadIncidentCompleted(e);
            });
        }
    }

    private onLoadIncidentCompleted(error?: any): void {
        if (error) {
            this.handleError(error);
        } else {
            this.onLoaded();
        }
    }

    public saveIncident() {
        if (this.incidentForm.valid) {
            this.showButtonLoading = true;
            this.data.incident.occurrenceDate = this.incidentForm.get('occurrenceDate').value;
            this.data.incident.isEventWOProhibition = this.incidentForm.get('isEventWOProhibition').value;
            this.data.incident.isExpunged = this.incidentForm.get('isExpunged').value;
            this.data.incident.hasSkippedLetterGeneration = !this.incidentForm.get('printLetter').value;

            if (this.showReferenceNumber) {
                this.data.incident.referenceNumber = this.incidentForm.get('referenceNumber').value;
            }

            if (this.showEditReceivedDate) {
                this.data.incident.receivedDate = this.incidentForm.get('receivedDate').value;
            }

            this.onSaveIncident(e => {
                this.showButtonLoading = false;
                if (e) {
                    this.handleValidationResult(e);
                } else {
                    this.dialogRef.close(true);
                }
            });
        }
    }

    public deleteIncident(): void {
        this.modalService.confirm()
            .title('Warning')
            .body('If you are sure you want to delete this incident record; press Delete again to complete the action.')
            .okBtn('Delete').cancelBtn('Cancel')
            .open().afterClosed()
            .pipe(filter(result => !!result))
            .subscribe(() => this.onDeleteIncident(() => this.dialogRef.close()));
    }

    public cancel() {
        this.dialogRef.close();
    }

    protected dateToString(value) {
        if (value != null) {
            var d = new Date(value);

            if (d) {
                return (d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear());
            } else {
                return value;
            }
        }

        return null;
    }

    protected handleError(error): void {
        if (error) {
            console.log('error', error);
        }
    }

    public uploadDocument(): void {
        window.alert('Upload document not implemented.');
    }

    public occurrenceDateBlur(event): void {
        var callback: (error?: any) => void;
        this.occurrenceDateChanged(this.incidentForm.get('occurrenceDate').value);
    }

    public occurrenceDateChanged(event): void {
        var callback: (error?: any) => void;
        this.data.incident.occurrenceDate = event;

        if (this.data.incident.occurrenceDate) {
            this.getCorrectIncidentType();
        }

        if (this.hasCdlCheckboxOnForm && this.data.incident.incidentNumberId == 0) {
            try {
                this.incidentService.getCDLStatusOnDate(this.data.incident).subscribe(r => {
                    if (this.incidentForm.get('isCDLHolder')) {
                        this.derivedCdl = r.result;
                        this.derivedCdlSet = true;
                        this.cdlRegistered = 2;
                        this.incidentForm.get('isCDLHolder').setValue(r.result);
                    }

                }, callback, callback);
            } catch (Exception) { }
        }
    }

    public reevaluateAndUpdateProhibition(id) {
        this.prohibitionService.reevaluateProhibition(id).subscribe(
            _ => {
                this.dialogRef.close();
            },
            e => {
                this.handleValidationResult(e);
                this.handleError(e);
            });
    }

    public reevaluatedBy() {
        var callback: (error?: any) => void;
        if (this.data.incident.incidentNumberId > 0) {
            try {
                this.incidentService.getLastReevaluated(this.data.incident.incidentNumberId).subscribe(r => {
                    this.lastReevaluatedBy = r.updatedBy;
                }, callback, callback);
            } catch (Exception) { }
        }
    }
}
